import React, { useState } from 'react'
import { useDispatch } from 'react-redux'

import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'

import { validateField, validateOptionalField, DESCRIPTION_MAX_LENGTH, validateRequiredResourceTags, collectTagData } from '../../utils/formValidation'
import ResourceTagsSection from '../shared/ResourceTagsSection'
import ResourceCheckboxes from '../shared/ResourceCheckboxes'
import TCAIREMFieldsSection from '../shared/TCAIREMFieldsSection'
import ResourceDescriptionField from '../shared/ResourceDescriptionField'
import { RequestFormStyle } from '../styles'
import { setErrorNotification } from '../../reducers/notificationReducer'

import {createPolicy} from "../../reducers/policiesReducer";
import useResourceSubmit from '../../hooks/useResourceSubmit'

const PolicyForm = () => {

    const dispatch = useDispatch()
    const { isSubmitting, submit } = useResourceSubmit()

    const [title, setTitle] = useState('')
    const [titleError, setTitleError] = useState(false)
    const [titleHelper, setTitleHelper] = useState('')
    const [description, setDescription] = useState('')
    const [descriptionError, setDescriptionError] = useState(false)
    const [descriptionHelper, setDescriptionHelper] = useState('')
    const [country, setCountry] = useState('')
    const [countryError, setCountryError] = useState(false)
    const [countryHelper, setCountryHelper] = useState('')
    const [institution, setInstitution] = useState('')
    const [institutionError, setInstitutionError] = useState(false)
    const [institutionHelper, setInstitutionHelper] = useState('')
    const [link, setLink] = useState('')
    const [linkError, setLinkError] = useState(false)
    const [linkHelper, setLinkHelper] = useState('')
    const [dateCreated, setDateCreated] = useState('')
    const [dateCreatedError, setDateCreatedError] = useState(false)
    const [dateCreatedHelper, setDateCreatedHelper] = useState('')
    const [dateLastAmended, setDateLastAmended] = useState('')
    const [dateLastAmendedError, setDateLastAmendedError] = useState(false)
    const [dateLastAmendedHelper, setDateLastAmendedHelper] = useState('')
    const [dateEnacted, setDateEnacted] = useState('')
    const [dateEnactedError, setDateEnactedError] = useState(false)
    const [dateEnactedHelper, setDateEnactedHelper] = useState('')
    const [contentTags, setContentTags] = useState([])
    const [medicalTags, setMedicalTags] = useState([])
    const [adminCategoryTags, setAdminCategoryTags] = useState([])
    const [experienceTags, setExperienceTags] = useState([])
    const [demographicTags, setDemographicTags] = useState([])
    const [showTagErrors, setShowTagErrors] = useState(false)
    const [isSeminalWork, setIsSeminalWork] = useState(false)
    const [inQuiz, setInQuiz] = useState(false)
    const [isTcairem, setIsTcairem] = useState(false)
    const [isPaid, setIsPaid] = useState(false)
    const [tcairemTags, setTcairemTags] = useState([])
    const [personaTags, setPersonaTags] = useState([])


    const reset = () => {
        setTitle('')
        setTitleError(false)
        setTitleHelper('')
        setDescription('')
        setDescriptionError(false)
        setDescriptionHelper('')
        setCountry('')
        setCountryError(false)
        setCountryHelper('')
        setInstitution('')
        setInstitutionError(false)
        setInstitutionHelper('')
        setLink('')
        setLinkError(false)
        setLinkHelper('')
        setDateCreated('')
        setDateCreatedError(false)
        setDateCreatedHelper('')
        setDateLastAmended('')
        setDateLastAmendedError(false)
        setDateLastAmendedHelper('')
        setDateEnacted('')
        setDateEnactedError(false)
        setDateEnactedHelper('')
        setContentTags([])
        setMedicalTags([])
        setAdminCategoryTags([])
        setExperienceTags([])
        setDemographicTags([])
        setShowTagErrors(false)
        setIsSeminalWork(false)
        setInQuiz(false)
        setIsTcairem(false)
        setIsPaid(false)
        setTcairemTags([])
        setPersonaTags([])
    }


    const handleSubmit = (event) => {
        event.preventDefault()
        setShowTagErrors(true)
        if (
            validateField(title, setTitleError, setTitleHelper, 500, 'Name') &&
            validateField(country, setCountryError, setCountryHelper, 500, 'Country') &&
            validateField(institution, setInstitutionError, setInstitutionHelper, 500, 'Institution') &&
            validateField(link, setLinkError, setLinkHelper) &&
            validateField(dateCreated, setDateCreatedError, setDateCreatedHelper) &&
            validateField(dateLastAmended, setDateLastAmendedError, setDateLastAmendedHelper) &&
            validateField(dateEnacted, setDateEnactedError, setDateEnactedHelper) &&
            validateOptionalField(description, setDescriptionError, setDescriptionHelper, DESCRIPTION_MAX_LENGTH, 'Description') &&
            validateRequiredResourceTags({
                contentTags,
                adminCategoryTags,
                experienceTags,
                demographicTags,
                tcairemTags,
                personaTags,
                isTcairem,
            })
        ) {

            const data = {
                title: title,
                description: description,
                country: country,
                institution: institution,
                link: link,
                created: dateCreated,
                updated: dateLastAmended,
                enacted: dateEnacted,
                ...collectTagData({ contentTags, medicalTags, adminCategoryTags, experienceTags, demographicTags, tcairemTags, personaTags, isTcairem }),
                is_seminal_work: isSeminalWork,
                in_quiz: inQuiz,
                is_tcairem: isTcairem,
                is_paid: isPaid,
            }

            submit(createPolicy(data), reset)
        } else {
            dispatch(setErrorNotification('Please fill all required fields before submission'))
        }
    }


    return (
        <>
            <Container {...RequestFormStyle.container}>
                <Typography {...RequestFormStyle.title.props} gutterBottom sx={RequestFormStyle.title.style}>
                    Policy Form
                </Typography>
                <form onSubmit={handleSubmit}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                                label="Name"
                                required
                                error={titleError}
                                helperText={titleHelper}
                                onBlur={()=>validateField(title, setTitleError, setTitleHelper, 500, 'Name')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                value={country}
                                onChange={e => setCountry(e.target.value)}
                                label="Country"
                                required
                                error={countryError}
                                helperText={countryHelper}
                                onBlur={()=>validateField(country, setCountryError, setCountryHelper, 500, 'Country')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                value={institution}
                                onChange={e => setInstitution(e.target.value)}
                                label="Institution"
                                required
                                error={institutionError}
                                helperText={institutionHelper}
                                onBlur={()=>validateField(institution, setInstitutionError, setInstitutionHelper, 500, 'Institution')}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                value={link}
                                type='url'
                                onChange={e => setLink(e.target.value)}
                                label="Link to Resource"
                                required
                                error={linkError}
                                helperText={linkHelper}
                                onBlur={()=>validateField(link, setLinkError, setLinkHelper)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                value={dateCreated}
                                type='date'
                                onChange={e => setDateCreated(e.target.value)}
                                label="Date Created"
                                required
                                error={dateCreatedError}
                                helperText={dateCreatedHelper}
                                onBlur={()=>validateField(dateCreated, setDateCreatedError, setDateCreatedHelper)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                value={dateLastAmended}
                                type='date'
                                onChange={e => setDateLastAmended(e.target.value)}
                                label="Date Last Amended"
                                required
                                error={dateLastAmendedError}
                                InputLabelProps={{ shrink: true }}
                                helperText={dateLastAmendedHelper}
                                onBlur={()=>validateField(dateLastAmended, setDateLastAmendedError, setDateLastAmendedHelper)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                value={dateEnacted}
                                type='date'
                                onChange={e => setDateEnacted(e.target.value)}
                                label="Date Enacted"
                                required
                                error={dateEnactedError}
                                InputLabelProps={{ shrink: true }}
                                helperText={dateEnactedHelper}
                                onBlur={()=>validateField(dateEnacted, setDateEnactedError, setDateEnactedHelper)}
                            />
                        </Grid>
                        <ResourceDescriptionField
                            value={description} setValue={setDescription}
                            error={descriptionError} setError={setDescriptionError}
                            helper={descriptionHelper} setHelper={setDescriptionHelper}
                        />
                        <ResourceTagsSection
                            contentTags={contentTags} setContentTags={setContentTags}
                            medicalTags={medicalTags} setMedicalTags={setMedicalTags}
                            adminCategoryTags={adminCategoryTags} setAdminCategoryTags={setAdminCategoryTags}
                            experienceTags={experienceTags} setExperienceTags={setExperienceTags}
                            demographicTags={demographicTags} setDemographicTags={setDemographicTags}
                            required={true}
                            forceShowRequiredErrors={showTagErrors}
                        />
                        <ResourceCheckboxes inQuiz={inQuiz} setInQuiz={setInQuiz} isSeminalWork={isSeminalWork} setIsSeminalWork={setIsSeminalWork} />
                        <TCAIREMFieldsSection
                            isTcairem={isTcairem} setIsTcairem={setIsTcairem}
                            isPaid={isPaid} setIsPaid={setIsPaid}
                            tcairemTags={tcairemTags} setTcairemTags={setTcairemTags}
                            personaTags={personaTags} setPersonaTags={setPersonaTags}
                            forceShowRequiredErrors={showTagErrors}
                        />
                        <Grid item xs={4}></Grid>
                        <Grid item xs={4}>
                            <Button sx={RequestFormStyle.button.style} {...RequestFormStyle.button.props} type='submit' disabled={isSubmitting}>Create Policy</Button>
                        </Grid>
                    </Grid>
                </form>
            </Container>
        </>
    )
}

export default PolicyForm
